import React from 'react';
import { motion } from 'framer-motion';
import { AlertTriangleIcon, ArrowRightIcon, ClockIcon, LockIcon, MailIcon, MessageSquareIcon, PhoneIcon, RotateCcwIcon, TargetIcon, UserIcon } from 'lucide-react';
import { Card } from '../ui/Card';
import { Badge } from '../ui/Badge';
import { SegmentedProgress } from '../ui/SegmentedProgress';
import type { Lead, LeadSource } from '../../types';
import { leadTypeFields } from '../../data/leadTypeFields';
import { formatAge, formatDuration, formatRelative } from '../../utils/format';
interface LeadWorkspaceProps {
  lead: Lead;
  source?: LeadSource;
  priorityIndex: number;
  priorityLabel: string;
  priorityReason: string;
  requiredCalls: number;
  ownerName: string;
  readOnly?: boolean;
  /** Next Lead stays locked until an activity is dispositioned on this lead. */
  canAdvance?: boolean;
  onCall?: () => void;
  onSms?: () => void;
  onEmail?: () => void;
  onNext?: () => void;
}
const detailLabel = 'text-[11px] font-semibold uppercase tracking-wide text-ink-400';
const detailValue = 'mt-1 text-sm font-medium text-ink-900';
export function LeadWorkspace({
  lead,
  source,
  priorityIndex,
  priorityLabel,
  priorityReason,
  requiredCalls,
  ownerName,
  readOnly = false,
  canAdvance = true,
  onCall,
  onSms,
  onEmail,
  onNext
}: LeadWorkspaceProps) {
  const overdue = lead.activities.filter(a => a.dueInMinutes !== undefined && a.dueInMinutes < 0);
  const nextActivity = lead.activities.find(a => a.dueInMinutes !== undefined && a.dueInMinutes >= 0);
  const callsRemaining = Math.max(0, requiredCalls - lead.callsToday);
  const typeFields = leadTypeFields[lead.leadType] ?? [];
  return <Card className="overflow-hidden" data-id="element-199" data-mp="1jjfyf6myvajz">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-brand-100 bg-brand-50 px-6 py-3.5" data-id="element-200" data-mp="1jiefyh3mfls8">
        <div className="flex items-center gap-3" data-id="element-201" data-mp="vrinriv96okg">
          <span className="tabular flex h-7 items-center rounded-lg bg-brand-600 px-2.5 text-xs font-bold text-white" data-id="element-202" data-mp="qvaeun7nfrlq">
            Priority {priorityIndex}
          </span>
          <div data-id="element-203" data-mp="2vvl9h07ns1i9">
            <p className="text-sm font-semibold text-brand-900" data-id="element-204" data-mp="2vvbk9euftydm">{priorityLabel}</p>
            <p className="text-xs text-brand-700/80" data-id="element-205" data-mp="30mtj9xcwhovy">{priorityReason}</p>
          </div>
        </div>
        <div className="flex items-center gap-4" data-id="element-206" data-mp="35lej3vo8q6jv">
          <span className="flex items-center gap-1.5 text-xs font-medium text-brand-700" data-id="element-207" data-mp="35l4uociug7ga">
            <TargetIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-208" data-mp="27z7cyhcag65e" />
            Presented by management rules
          </span>
          {!readOnly ? <span className="flex items-center gap-1.5 rounded-full bg-white px-2.5 py-1 text-xs font-medium text-brand-800 ring-1 ring-inset ring-brand-200" data-id="element-209" data-mp="2hwbrbuzw6xbn">
              <LockIcon className="h-3 w-3" aria-hidden="true" data-id="element-210" data-mp="2lz058h3gqufh" />
              Locked in place
            </span> : null}
        </div>
      </div>

      <div className="px-6 pb-6 pt-5" data-id="element-211" data-mp="16ojm6np0x5m">
        <motion.div key={lead.id} initial={{
        opacity: 0,
        y: 8
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.25,
        ease: 'easeOut'
      }} data-id="element-212" data-mp="15mvp1lkwg6h">
          <div className="flex flex-wrap items-start justify-between gap-4" data-id="element-213" data-mp="1hoowelhhhurx">
            <div data-id="element-214" data-mp="1cw59gxwwpnag">
              <h1 className="text-2xl font-semibold tracking-tight text-ink-900" data-id="element-215" data-mp="1cvlwlvm45p3a">{lead.name}</h1>
              <div className="mt-2 flex flex-wrap items-center gap-2" data-id="element-216" data-mp="17xkfvhc5u1uz">
                <Badge tone="brand" data-id="element-217" data-mp="130265oqbjilx">{lead.leadType}</Badge>
                <Badge tone="neutral" data-id="element-218" data-mp="xzw9tv93t7xi">{lead.status}</Badge>
                <Badge tone="neutral" data-id="element-219" data-mp="xycss2fmisuo">{source?.name ?? 'Unassigned Source'}</Badge>
                <Badge tone="neutral" data-id="element-220" data-mp="tzcbwpgpi0ab">{formatAge(lead.ageMinutes)}</Badge>
              </div>
            </div>
            <div className="flex items-center gap-2 rounded-xl border border-ink-200 px-3 py-2" data-id="element-221" data-mp="p0j8xqz1proe">
              <UserIcon className="h-4 w-4 text-ink-400" aria-hidden="true" data-id="element-222" data-mp="2ob0xhahg113o" />
              <div className="leading-tight" data-id="element-223" data-mp="2jczcgk0girpk">
                <p className="text-[11px] text-ink-500" data-id="element-224" data-mp="2jb65f3lxm5h1">Assigned Loan Officer</p>
                <p className="text-sm font-medium text-ink-900" data-id="element-225" data-mp="1g2v0j0r9wd1x">{ownerName}</p>
              </div>
            </div>
          </div>

          <div className="mt-5 grid gap-4 rounded-xl border border-ink-200 bg-ink-50/60 px-5 py-4 sm:grid-cols-2 lg:grid-cols-4" data-id="element-226" data-mp="1kz26gtjo5ojv">
            <div data-id="element-227" data-mp="1c08claxq3ca0">
              <p className={detailLabel} data-id="element-228" data-mp="1bzoy647a760q">Phone</p>
              <p className={`${detailValue} tabular`} data-id="element-229" data-mp="1c2krff86p6iu">{lead.phone}</p>
            </div>
            <div className="min-w-0" data-id="element-230" data-mp="x9r3d5euji2k">
              <p className={detailLabel} data-id="element-231" data-mp="1233srhrcnswd">Email</p>
              <p className={`${detailValue} truncate`} data-id="element-232" data-mp="123vbvt3rfy5r">{lead.email}</p>
            </div>
            <div data-id="element-233" data-mp="ocqjnc2q285g">
              <p className={detailLabel} data-id="element-234" data-mp="o92q4cxa1lqq">Last Contact Attempt</p>
              <p className={detailValue} data-id="element-235" data-mp="sdk6010inew8">
                {lead.lastContactMinutes !== undefined ? formatRelative(lead.lastContactMinutes) : 'No attempts yet'}
              </p>
            </div>
            <div data-id="element-236" data-mp="je5fx2o9h6c9">
              <p className={detailLabel} data-id="element-237" data-mp="3vvos8f5oy4tj">Next Required Activity</p>
              <p className={detailValue} data-id="element-238" data-mp="3vvx5471tda4q">
                {overdue.length > 0 ? overdue[0].title : nextActivity ? `${nextActivity.title} · in ${formatDuration(nextActivity.dueInMinutes ?? 0)}` : callsRemaining > 0 ? `Call attempt ${lead.callsToday + 1} of ${requiredCalls}` : 'Cadence complete'}
              </p>
            </div>
          </div>

          <div className="mt-4 rounded-xl border border-ink-200 px-5 py-4" data-id="element-239" data-mp="dp0mqo6qbfnc">
            <div className="flex items-center justify-between" data-id="element-240" data-mp="seolpirfu27w">
              <p className="text-xs font-semibold uppercase tracking-wide text-ink-500" data-id="element-241" data-mp="sh12hslgm6jd">
                {lead.leadType} Details
              </p>
              <span className="text-[11px] text-ink-400" data-id="element-242" data-mp="11euz74n4z272">Fields shown for this lead type</span>
            </div>
            <dl className="mt-3 grid gap-4 sm:grid-cols-3 lg:grid-cols-5" data-id="element-243" data-mp="xf2l6cez43ub">
              {typeFields.map(field => <div key={field.key} data-id="element-244" data-mp="14ispesywlslz">
                  <dt className={detailLabel} data-id="element-245" data-mp="19gc39rrow3de">{field.label}</dt>
                  <dd className={detailValue} data-id="element-246" data-mp="19gltnggnccjm">{lead.attributes[field.key] ?? '—'}</dd>
                </div>)}
            </dl>
          </div>

          {overdue.length > 0 ? <ul className="mt-4 space-y-2" data-id="element-247" data-mp="1j7os0ai6ydf7">
              {overdue.map(activity => <li key={activity.id} className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3" data-id="element-248" data-mp="1n6xnpkwsklde">
                  <AlertTriangleIcon className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" aria-hidden="true" data-id="element-249" data-mp="1x6ya5fpyjhlt" />
                  <div className="min-w-0" data-id="element-250" data-mp="2rvra0veyxrv">
                    <p className="text-sm font-semibold text-amber-900" data-id="element-251" data-mp="2t71ojjd73ry">
                      Past Due{' '}
                      {activity.type === 'event' ? 'Appointment' : activity.type === 'task' ? 'Task' : 'Activity'}{' '}
                      · {activity.title}
                    </p>
                    <p className="mt-0.5 text-xs text-amber-800" data-id="element-252" data-mp="2c9pyafe3315q">
                      {formatDuration(activity.dueInMinutes ?? 0)} past due
                      {activity.detail ? ` · ${activity.detail}` : ''}
                    </p>
                  </div>
                </li>)}
            </ul> : null}

          <div className="mt-5 rounded-xl border border-ink-200 px-5 py-4" data-id="element-253" data-mp="2zwejb9ye39qk">
            <div className="flex flex-wrap items-center justify-between gap-2" data-id="element-254" data-mp="2uxuwl7bz1tx5">
              <p className="text-sm font-semibold text-ink-900" data-id="element-255" data-mp="2pz9vz6st5886">
                Calls Today{' '}
                <span className="tabular text-ink-500" data-id="element-256" data-mp="1jlakfw6ouovz">
                  {lead.callsToday} of {requiredCalls} Complete
                </span>
              </p>
              <span className="flex items-center gap-1.5 text-xs font-medium text-ink-500" data-id="element-257" data-mp="3enfd5rkw5snn">
                <ClockIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-258" data-mp="3eps42u8mndcu" />
                {callsRemaining > 0 ? `${callsRemaining} more required to clear today's cadence` : 'Cadence satisfied — lead exits the queue'}
              </span>
            </div>
            <SegmentedProgress className="mt-3" completed={lead.callsToday} total={requiredCalls} tone={callsRemaining === 0 ? 'emerald' : 'brand'} data-id="element-259" data-mp="39qf9fnbil1zp" />
            {callsRemaining > 0 ? <p className="mt-2.5 flex items-center gap-1.5 text-xs text-ink-500" data-id="element-260" data-mp="3nn2hom8x3ekx">
                <RotateCcwIcon className="h-3.5 w-3.5 text-ink-400" aria-hidden="true" data-id="element-261" data-mp="3smowh04e92vu" />
                Recycles to the back of your queue after this pass — no stacked tasks to clean up.
              </p> : null}
          </div>

          {!readOnly ? <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4" data-id="element-262" data-mp="ozy60jfmsiq8">
              <button type="button" onClick={onCall} className="flex items-center justify-center gap-2 rounded-xl bg-brand-600 px-5 py-4 text-sm font-semibold text-white shadow-card transition-all hover:bg-brand-700 active:scale-[0.99]" data-id="element-263" data-mp="29mo987lba2q2">
                <PhoneIcon className="h-4 w-4" aria-hidden="true" data-id="element-264" data-mp="2eg0r7yvhjb9u" />
                Call {lead.phone}
              </button>
              <button type="button" onClick={onSms} className="flex items-center justify-center gap-2 rounded-xl border border-ink-200 bg-white px-5 py-4 text-sm font-semibold text-ink-800 transition-all hover:border-ink-300 hover:bg-ink-50 active:scale-[0.99]" data-id="element-265" data-mp="1lqjuwq4qva8c">
                <MessageSquareIcon className="h-4 w-4" aria-hidden="true" data-id="element-266" data-mp="3b4qde1e5aq1m" />
                Send SMS
              </button>
              <button type="button" onClick={onEmail} className="flex items-center justify-center gap-2 rounded-xl border border-ink-200 bg-white px-5 py-4 text-sm font-semibold text-ink-800 transition-all hover:border-ink-300 hover:bg-ink-50 active:scale-[0.99]" data-id="element-267" data-mp="3734sptvdd7gv">
                <MailIcon className="h-4 w-4" aria-hidden="true" data-id="element-268" data-mp="14j1tojjcgyvt" />
                Send Email
              </button>
              <button type="button" onClick={onNext} disabled={!canAdvance} title={canAdvance ? 'Advance to the next lead' : 'Log a call, SMS, or email to advance'} className="flex items-center justify-center gap-2 rounded-xl bg-ink-900 px-5 py-4 text-sm font-semibold text-white transition-all hover:bg-ink-800 active:scale-[0.99] disabled:cursor-not-allowed disabled:bg-ink-200 disabled:text-ink-500" data-id="element-269" data-mp="umysph08b6ag">
                {canAdvance ? null : <LockIcon className="h-4 w-4" aria-hidden="true" data-id="element-270" data-mp="2ikr7v3jyhskf" />}
                Next Lead
                {canAdvance ? <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-271" data-mp="2njkmizazbpm6" /> : null}
              </button>
            </div> : null}

          {!readOnly ? <p className={`mt-3 text-xs ${canAdvance ? 'text-emerald-700' : 'text-ink-500'}`} aria-live="polite" data-id="element-272" data-mp="2sbccp8q83tbs">
              {canAdvance ? 'Activity logged — Next Lead unlocked.' : 'Log one activity — call, SMS, or email — to unlock Next Lead. A quick disposition is enough.'}
            </p> : null}
        </motion.div>
      </div>
    </Card>;
}